import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage'; // This is for AppImage component, not react-pdf Image
import Button from '../../../components/ui/Button';
import ShipmentTrackingModal from '../../../components/ShipmentTrackingModal';
import { checkoutService } from '../../../services/checkoutService';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { formatPrice } from '../../../utils/formatters';
import { Document, Page, Text, View, StyleSheet, pdf, Image as PDFImage } from '@react-pdf/renderer'; // Renamed Image to PDFImage

// Create styles for the PDF document
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#f8f9fa', // Light gray background for the page
    padding: 30,
    fontFamily: 'Helvetica',
  },
  logo: {
    width: 150, // Adjust width as needed
    height: 40, // Adjust height as needed
    alignSelf: 'center', // Center the logo
    marginBottom: 20,
    objectFit: 'contain', // Ensure the image fits within the bounds
  },
  headerSection: {
    backgroundColor: '#007bff', // Primary blue background for header
    color: '#ffffff',
    padding: 20,
    marginBottom: 20,
    textAlign: 'center',
    borderRadius: 8,
  },
  headerText: {
    fontSize: 28,
    fontWeight: 'bold',
  },
  subHeaderText: {
    fontSize: 14,
    color: '#ffffff',
  },
  section: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    marginBottom: 15,
    border: '1px solid #dee2e6',
    overflow: 'hidden', // Ensures borders are contained
  },
  sectionHeader: {
    fontSize: 16,
    fontWeight: 'bold',
    padding: 15,
    backgroundColor: '#e9ecef', // Light gray for section header background
    borderBottom: '1px solid #dee2e6',
    color: '#343a40',
  },
  sectionContent: {
    padding: 15,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  infoLabel: {
    fontSize: 11,
    color: '#6c757d', // Muted text color
  },
  infoValue: {
    fontSize: 11,
    fontWeight: 'medium',
    color: '#343a40',
  },
  itemHeader: {
    flexDirection: 'row',
    borderBottom: '1px solid #dee2e6',
    paddingBottom: 8,
    marginBottom: 8,
  },
  itemHeaderText: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#343a40',
  },
  itemRow: {
    flexDirection: 'row',
    borderBottom: '1px dashed #e9ecef', // Dashed border for items
    paddingBottom: 8,
    marginBottom: 8,
    alignItems: 'center',
  },
  itemRowLast: {
    borderBottom: 'none',
  },
  itemImage: { // New style for item images
    width: 40,
    height: 40,
    marginRight: 10,
    objectFit: 'cover',
    borderRadius: 4,
  },
  itemDetails: {
    flexGrow: 1,
    maxWidth: '50%', // Adjusted width to make space for image
  },
  itemName: {
    fontSize: 12,
    fontWeight: 'medium',
    color: '#343a40',
  },
  itemMeta: {
    fontSize: 10,
    color: '#6c757d',
  },
  itemQuantity: {
    width: '15%',
    textAlign: 'center',
    fontSize: 11,
    color: '#343a40',
  },
  itemPrice: {
    width: '25%',
    textAlign: 'right',
    fontSize: 12,
    fontWeight: 'medium',
    color: '#343a40',
  },
  summaryGrid: {
    flexDirection: 'column',
  },
  summaryLine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  summaryLabel: {
    fontSize: 12,
    color: '#343a40',
  },
  summaryValue: {
    fontSize: 12,
    fontWeight: 'medium',
    color: '#343a40',
  },
  totalLine: {
    borderTop: '1px solid #dee2e6',
    paddingTop: 8,
    marginTop: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  totalLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#343a40',
  },
  totalValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#007bff', // Primary blue for total
  },
  footer: {
    fontSize: 10,
    textAlign: 'center',
    marginTop: 30,
    color: '#6c757d',
  },
  addressBlock: {
    marginBottom: 10,
  },
  addressTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  addressText: {
    fontSize: 11,
    color: '#343a40',
  },
});

// Helper to format shipper object (Text-only for PDF)
const formatShipperInfo = (shipper) => {
  if (!shipper) return null;
  if (typeof shipper === 'string') return shipper;
  const { carrier, service_type, tracking_number } = shipper;
  
  const serviceNames = {
    'UPS': {
      '01': 'Next Day Air', '02': '2nd Day Air', '03': 'Ground', '12': '3 Day Select',
      '13': 'Next Day Air Saver', '14': 'Next Day Air Early', '65': 'UPS Saver'
    },
    'FedEx': {
      'FEDEX_GROUND': 'FedEx Ground', 'GROUND_HOME_DELIVERY': 'Home Delivery',
      'PRIORITY_OVERNIGHT': 'Priority Overnight', 'STANDARD_OVERNIGHT': 'Standard Overnight',
      'FEDEX_2_DAY': 'FedEx 2 Day', 'FEDEX_EXPRESS_SAVER': 'Express Saver'
    }
  };

  const serviceName = serviceNames[carrier]?.[service_type] || service_type || 'Standard';
  const carrierName = carrier || 'Standard';
  const actionVerb = tracking_number ? 'Shipped via' : 'Shipping via';

  if (!carrier && !service_type && !tracking_number) return 'Standard';
  return `${actionVerb} ${carrierName} - ${serviceName}${tracking_number ? ` [${tracking_number}]` : ''}`;
};

// Component helper for rendering shipper info with links (JSX)
const renderShipperDisplay = (shipper) => {
  if (!shipper) return null;
  
  let info = shipper;
  if (typeof shipper === 'string') {
    try { info = JSON.parse(shipper); } catch (e) { return shipper; }
  }

  const { carrier, service_type, tracking_number, label_url, tracking_url } = info;
  
  const serviceNames = {
    'UPS': {
      '01': 'Next Day Air', '02': '2nd Day Air', '03': 'Ground', '12': '3 Day Select',
      '13': 'Next Day Air Saver', '14': 'Next Day Air Early', '65': 'UPS Saver'
    },
    'FedEx': {
      'FEDEX_GROUND': 'FedEx Ground', 'GROUND_HOME_DELIVERY': 'Home Delivery',
      'PRIORITY_OVERNIGHT': 'Priority Overnight', 'STANDARD_OVERNIGHT': 'Standard Overnight',
      'FEDEX_2_DAY': 'FedEx 2 Day', 'FEDEX_EXPRESS_SAVER': 'Express Saver'
    }
  };

  const serviceName = serviceNames[carrier]?.[service_type] || service_type || 'Standard';
  const carrierName = carrier || '';
  const actionVerb = tracking_number ? 'Shipped via' : 'Shipping via';
  
  // Determine the best tracking URL
  let trackUrl = null;
  if (tracking_url && (tracking_url.startsWith('http') || tracking_url.includes('://'))) trackUrl = tracking_url;
  else if (label_url && (label_url.startsWith('http') || label_url.includes('://')) && !label_url.startsWith('data:')) trackUrl = label_url;
  
  if (!trackUrl && tracking_number && carrierName) {
      if (carrierName === 'FedEx') trackUrl = `https://www.fedex.com/fedextrack/?trknbr=${tracking_number}`;
      else if (carrierName === 'UPS') trackUrl = `https://www.ups.com/track?loc=en_US&tracknum=${tracking_number}`;
  }

  return (
    <span className="flex flex-wrap items-center gap-1">
      <span>{actionVerb} {carrierName}{carrierName ? ' - ' : ''}{serviceName}</span>
      {tracking_number && trackUrl && (
        <a 
          href={trackUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline font-mono ml-1"
          onClick={(e) => e.stopPropagation()}
        >
          [{tracking_number}]
        </a>
      )}
      {tracking_number && !trackUrl && <span className="font-mono ml-1">[{tracking_number}]</span>}
    </span>
  );
};

// Create Document Component
const OrderReceiptDocument = ({ purchase }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* OpenGadget Logo */}
      <PDFImage src="/path/to/your/opengadget-logo.png" style={styles.logo} />

      {/* Header Section */}
      <View style={styles.headerSection}>
        <Text style={styles.headerText}>Order Confirmation</Text>
        <Text style={styles.subHeaderText}>Thank you for your purchase!</Text>
      </View>

      {/* Order Information */}
      <View style={styles.section}>
        <Text style={styles.sectionHeader}>Order Details</Text>
        <View style={styles.sectionContent}>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Order Number:</Text>
            <Text style={styles.infoValue}>{purchase?.order_number || purchase?.id}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Order Date:</Text>
            <Text style={styles.infoValue}>
              {new Date(purchase?.date)?.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Status:</Text>
            <Text style={styles.infoValue}>
              {purchase?.status?.charAt(0)?.toUpperCase() + purchase?.status?.slice(1)}
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Payment Status:</Text>
            <Text style={styles.infoValue}>
              {purchase?.paymentStatus?.charAt(0)?.toUpperCase() + purchase?.paymentStatus?.slice(1)}
            </Text>
          </View>
        </View>
      </View>

      {/* Order Items */}
      <View style={styles.section}>
        <Text style={styles.sectionHeader}>Items Ordered</Text>
        <View style={styles.sectionContent}>
          <View style={styles.itemHeader}>
            <Text style={[styles.itemHeaderText, { flexGrow: 1, maxWidth: '60%' }]}>Item</Text>
            <Text style={[styles.itemHeaderText, { width: '15%', textAlign: 'center' }]}>Qty</Text>
            <Text style={[styles.itemHeaderText, { width: '25%', textAlign: 'right' }]}>Price</Text>
          </View>
          {purchase?.items?.map((item, index) => (
            <View key={index} style={[styles.itemRow, index === purchase.items.length - 1 ? styles.itemRowLast : {}]}>
              {item?.image && <PDFImage src={item?.image} style={styles.itemImage} />}
              <View style={styles.itemDetails}>
                <Text style={styles.itemName}>{item?.name}</Text>
                <Text style={styles.itemMeta}>
                  {item?.type} | {formatPrice(item?.price)} each
                </Text>
                {item?.shipper && (
                  <Text style={styles.itemMeta}>Shipped via: {formatShipperInfo(item?.shipper)}</Text>
                )}
              </View>
              <Text style={styles.itemQuantity}>{item?.quantity}</Text>
              <Text style={styles.itemPrice}>{formatPrice(item?.price * item?.quantity)}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* Shipping and Billing Addresses */}
      {(purchase?.shipping_address || purchase?.billing_address) && (
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>Addresses</Text>
          <View style={[styles.sectionContent, { flexDirection: 'row', justifyContent: 'space-between' }]}>
            {purchase?.shipping_address && (
              <View style={[styles.addressBlock, { width: '48%' }]}>
                <Text style={styles.addressTitle}>Shipping Address:</Text>
                <Text style={styles.addressText}>{purchase?.shipping_address?.first_name} {purchase?.shipping_address?.last_name}</Text>
                <Text style={styles.addressText}>{purchase?.shipping_address?.address_line_1}</Text>
                {purchase?.shipping_address?.address_line_2 && <Text style={styles.addressText}>{purchase?.shipping_address?.address_line_2}</Text>}
                <Text style={styles.addressText}>{purchase?.shipping_address?.city}, {purchase?.shipping_address?.state} {purchase?.shipping_address?.postal_code}</Text>
                <Text style={styles.addressText}>{purchase?.shipping_address?.country}</Text>
                {purchase?.shipping_address?.phone && <Text style={styles.addressText}>Phone: {purchase?.shipping_address?.phone}</Text>}
              </View>
            )}
            {purchase?.billing_address && (purchase?.billing_address?.id !== purchase?.shipping_address?.id || !purchase?.shipping_address) && (
              <View style={[styles.addressBlock, { width: '48%' }]}>
                <Text style={styles.addressTitle}>Billing Address:</Text>
                <Text style={styles.addressText}>{purchase?.billing_address?.first_name} {purchase?.billing_address?.last_name}</Text>
                <Text style={styles.addressText}>{purchase?.billing_address?.address_line_1}</Text>
                {purchase?.billing_address?.address_line_2 && <Text style={styles.addressText}>{purchase?.billing_address?.address_line_2}</Text>}
                <Text style={styles.addressText}>{purchase?.billing_address?.city}, {purchase?.billing_address?.state} {purchase?.billing_address?.postal_code}</Text>
                <Text style={styles.addressText}>{purchase?.billing_address?.country}</Text>
              </View>
            )}
          </View>
        </View>
      )}


      {/* Order Summary */}
      <View style={styles.section}>
        <Text style={styles.sectionHeader}>Order Summary</Text>
        <View style={[styles.sectionContent, styles.summaryGrid]}>
          <View style={styles.summaryLine}>
            <Text style={styles.summaryLabel}>Subtotal:</Text>
            <Text style={styles.summaryValue}>{formatPrice(purchase?.subtotal)}</Text>
          </View>
          <View style={styles.summaryLine}>
            <Text style={styles.summaryLabel}>Tax:</Text>
            <Text style={styles.summaryValue}>{formatPrice(purchase?.taxAmount)}</Text>
          </View>
          <View style={styles.summaryLine}>
            <Text style={styles.summaryLabel}>Shipping:</Text>
            <Text style={styles.summaryValue}>{formatPrice(purchase?.shippingAmount || 0)}</Text>
          </View>
          
          {purchase?.discountAmount > 0 && (
            <View style={styles.summaryLine}>
              <Text style={styles.summaryLabel}>Discount:</Text>
              <Text style={styles.summaryValue}>-{formatPrice(purchase?.discountAmount)}</Text>
            </View>
          )}

          {purchase?.appliedCredit > 0 && (
            <View style={styles.summaryLine}>
              <Text style={[styles.summaryLabel, { color: '#10b981', fontWeight: 'bold' }]}>Store Credit Applied:</Text>
              <Text style={[styles.summaryValue, { color: '#10b981', fontWeight: 'bold' }]}>-{formatPrice(purchase?.appliedCredit)}</Text>
            </View>
          )}

          <View style={styles.totalLine}>
            <Text style={styles.totalLabel}>Total Paid:</Text>
            <Text style={styles.totalValue}>{formatPrice((parseFloat(purchase?.subtotal) || 0) + (parseFloat(purchase?.taxAmount) || 0) + (parseFloat(purchase?.shippingAmount) || 0) - (parseFloat(purchase?.appliedCredit) || 0))}</Text>
          </View>
        </View>
      </View>

      {/* Footer */}
      <Text style={styles.footer}>
        This is an automatically generated receipt. For support, please contact us.
      </Text>
    </Page>
  </Document>
);

const PurchaseHistory = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [filter, setFilter] = useState('all');
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [verifiedUserId, setVerifiedUserId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Active shipment tracking modal state & generator
  const [activeTracking, setActiveTracking] = useState(null);

  const handleTrackingClick = (shipper, parentItem = null) => {
    let info = shipper;
    if (typeof shipper === 'string') {
      try { info = JSON.parse(shipper); } catch (e) { info = {}; }
    }
    setActiveTracking({
      carrier: info?.carrier || 'Carrier',
      trackingNumber: info?.tracking_number,
      serviceType: info?.service_type || 'Standard',
      parentItem
    });
  };

  // ✅ NEW: Independent session verification as fallback
  useEffect(() => {
    const verifySession = async () => {
      try {
        // Try to get session directly from Supabase
        const { data: { session }, error } = await supabase?.auth?.getSession();
        
        if (error) {
          console.error('🔍 PurchaseHistory - Session verification error:', error);
          return;
        }
        
        if (session?.user?.id) {
          console.log('✅ PurchaseHistory - Session verified directly:', session?.user?.id);
          setVerifiedUserId(session?.user?.id);
        } else {
          console.log('❌ PurchaseHistory - No session found in direct check');
        }
      } catch (err) {
        console.error('🔍 PurchaseHistory - Session verification failed:', err);
      }
    };

    verifySession();
  }, []);

  useEffect(() => {
    // Reset error state when auth state changes
    setError('');
    setPurchases([]);
    
    // Wait for auth loading to complete
    if (authLoading) {
      console.log('⏳ PurchaseHistory - Auth still loading...');
      setLoading(true);
      return;
    }

    // ✅ ENHANCED: Check both context user and verified session
    const effectiveUserId = user?.id || verifiedUserId;
    
    if (!effectiveUserId) {

      setLoading(false);
      setError('Please log in to view your purchase history');
      return;
    }


    loadPurchaseHistory(effectiveUserId);
  }, [user?.id, verifiedUserId, authLoading]);

  const loadPurchaseHistory = async (userId) => {
    try {
      setLoading(true);
      setError('');
      
      if (!userId || userId === 'undefined' || userId === 'null') {
        console.error('❌ PurchaseHistory - Invalid user ID:', userId);
        setError('Invalid session: Please log out and log in again');
        setLoading(false);
        return;
      }
      
      // 1. Fetch raw orders simply to bypass brittle complex PostgREST joins
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;

      if (!orders || orders.length === 0) {
        setPurchases([]);
        setLoading(false);
        return;
      }

      // 2. Fetch order items
      const orderIds = orders.map(o => o.id);
      const { data: orderItems, error: itemsError } = await supabase
        .from('order_items')
        .select('*')
        .in('order_id', orderIds);

      if (itemsError) throw itemsError;

      // 3. Fetch referenced seller products and seller components
      const sellerProductIds = (orderItems || []).map(i => i.product_id).filter(Boolean);
      const sellerComponentIds = (orderItems || []).map(i => i.component_id).filter(Boolean);

      const [productsRes, componentsRes] = await Promise.all([
        sellerProductIds.length > 0 
          ? supabase.from('seller_products').select('id, seller_image_url, products(id, name, thumbnail_path)').in('id', sellerProductIds)
          : Promise.resolve({ data: [] }),
        sellerComponentIds.length > 0
          ? supabase.from('seller_components').select('id, custom_name, images_url, components(id, name)').in('id', sellerComponentIds)
          : Promise.resolve({ data: [] })
      ]);

      const sellerProductsMap = new Map((productsRes.data || []).map(p => [p.id, p]));
      const sellerComponentsMap = new Map((componentsRes.data || []).map(c => [c.id, c]));

      // 4. Map raw orders to the expected UI format
      const mappedOrders = orders.map(order => {
        const items = (orderItems || [])
          .filter(item => item.order_id === order.id)
          .map(item => {
            const isComponent = !!item.component_id;
            
            // Resolve details from maps or fallback
            const sProd = sellerProductsMap.get(item.product_id);
            const sComp = sellerComponentsMap.get(item.component_id);

            const name = isComponent 
              ? (sComp?.custom_name || sComp?.components?.name || item.name || 'Component')
              : (sProd?.products?.name || item.name || 'Product');
            
            let rawPath = isComponent 
              ? (sComp?.images_url || '')
              : (sProd?.seller_image_url || sProd?.products?.thumbnail_path || '');
              
            if (Array.isArray(rawPath)) {
              rawPath = rawPath[0] || '';
            }
              
            const image = (rawPath && typeof rawPath === 'string' && (rawPath.startsWith('http') || rawPath.startsWith('/assets') || rawPath.startsWith('/components'))) 
              ? rawPath 
              : (rawPath && typeof rawPath === 'string' ? supabase.storage.from('product-thumbnails').getPublicUrl(rawPath).data.publicUrl : '/assets/placeholder-product.png');

            return {
              id: item.id,
              name,
              quantity: item.quantity,
              price: item.total_price || (item.unit_price * item.quantity),
              type: isComponent ? 'Component' : 'Product',
              image,
              imageAlt: name,
              shipper: item.shipper,
              itemStatus: item.item_status || 'pending',
              item_status: item.item_status || 'pending',
              manufacturingStatus: item.manufacturing_status || 'Pending',
              manufacturing_status: item.manufacturing_status || 'Pending',
              productId: item.product_id,
              componentId: item.component_id,
              component_id: item.component_id
            };
          });

        const allItemsDelivered = items.length > 0 && items.every(i => i.itemStatus === 'delivered' || i.item_status === 'delivered');
        const computedStatus = allItemsDelivered ? 'delivered' : order.status;

        return {
          id: order.id,
          order_number: order.order_number,
          status: computedStatus,
          date: order.created_at,
          subtotal: order.subtotal || 0,
          taxAmount: order.tax_amount || 0,
          shippingAmount: order.shipping_amount || 0,
          appliedCredit: order.applied_credit || 0,
          items
        };
      });

      setPurchases(mappedOrders);
      setError('');

      // Background Carrier Sync: Check live delivery status for orders with tracking numbers
      setTimeout(() => {
        mappedOrders.forEach(ord => {
          (ord.items || []).forEach(it => {
            let sInfo = it.shipper;
            if (typeof sInfo === 'string') {
              try { sInfo = JSON.parse(sInfo); } catch(e) {}
            }
            if (sInfo?.tracking_number && sInfo?.carrier && it.itemStatus !== 'delivered') {
              fetch(`/api/v1/shipping/track/${encodeURIComponent(sInfo.carrier)}/${encodeURIComponent(sInfo.tracking_number)}`)
                .then(r => r.json())
                .then(tData => {
                  if (tData.success && (tData.is_delivered || (tData.status || '').toLowerCase() === 'delivered')) {
                    setPurchases(curr => curr.map(cp => {
                      if (cp.id === ord.id) {
                        const newItems = cp.items.map(ci => ci.id === it.id ? { ...ci, itemStatus: 'delivered', item_status: 'delivered' } : ci);
                        const isAllDelivered = newItems.every(ci => ci.itemStatus === 'delivered' || ci.item_status === 'delivered');
                        return { ...cp, status: isAllDelivered ? 'delivered' : cp.status, items: newItems };
                      }
                      return cp;
                    }));
                  }
                })
                .catch(() => {});
            }
          });
        });
      }, 300);
    } catch (err) {
      console.error('❌ PurchaseHistory - Error loading purchase history:', err);
      setError(err?.message || 'Failed to load purchase history');
    } finally {
      setLoading(false);
    }
  };

  // ✅ ENHANCED: Retry with session refresh
  const handleRetry = async () => {
    try {
      // Force refresh session before retry
      const { data: { session }, error } = await supabase?.auth?.getSession();
      
      if (error) throw error;
      
      if (session?.user?.id) {
        setVerifiedUserId(session?.user?.id);
        await loadPurchaseHistory(session?.user?.id);
      } else {
        setError('Please log in to view your purchase history');
      }
    } catch (err) {
      console.error('❌ PurchaseHistory - Retry failed:', err);
      setError('Failed to refresh session. Please try logging in again.');
    }
  };

  // ✅ NEW: Function to show order details
  const handleShowDetails = (purchase) => {
    setSelectedOrder(purchase);
  };

  // ✅ NEW: Function to close order details modal
  const handleCloseDetails = () => {
    setSelectedOrder(null);
  };

  // ✅ UPDATED: Include shipper info per item in PDF
  const handleDownloadPDF = async (purchase) => {
    try {
      const blob = await pdf(<OrderReceiptDocument purchase={purchase} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `receipt-${purchase.order_number || purchase.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('❌ Error generating PDF:', err);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // ✅ NEW: Handler to navigate to model catalog
  const handleBrowseModels = () => {
    navigate('/model-catalog');
  };

  const filterOptions = [
    { value: 'all', label: 'All Orders' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'completed', label: 'Completed' },
    { value: 'pending', label: 'Pending' },
    { value: 'refunded', label: 'Refunded' }
  ];

  const getStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'completed':
      case 'delivered': return 'text-emerald-700 bg-emerald-100/80 border border-emerald-300 font-bold';
      case 'shipped':
      case 'in transit': return 'text-blue-700 bg-blue-100/80 border border-blue-300 font-bold';
      case 'pending': return 'text-amber-700 bg-amber-100/80 border border-amber-300 font-bold';
      case 'refunded': return 'text-error bg-error/10 border border-red-200 font-bold';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const filteredPurchases = filter === 'all' ?
    purchases :
    purchases?.filter((purchase) => {
      const s = (purchase?.status || '').toLowerCase();
      if (filter === 'completed') return s === 'completed' || s === 'delivered';
      return s === filter;
    });

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6 shadow-soft">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-sm text-muted-foreground">Loading purchase history...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-lg border border-border p-6 shadow-soft">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">Error Loading Orders</h3>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <div className="flex items-center justify-center gap-3">
              <Button variant="default" onClick={handleRetry}>
                Try Again
              </Button>
              {error?.includes('log in') && (
                <Button variant="outline" onClick={() => window.location.href = '/signin'}>
                  Go to Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card rounded-lg border border-border p-6 shadow-soft">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">Purchase History</h2>
          
          <div className="flex items-center space-x-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e?.target?.value)}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring">

              {filterOptions?.map((option) =>
              <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              )}
            </select>
            
            <Button variant="outline" size="sm" iconName="Download">
              Export
            </Button>
          </div>
        </div>
        <div className="space-y-4">
          {filteredPurchases?.map((purchase) => (
            <div key={purchase?.id} className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-smooth">
              <div className="flex items-start justify-between mb-4">
                <div className="space-y-1">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-sm font-medium text-foreground">
                      Order #{purchase?.order_number || purchase?.id}
                    </h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(purchase?.status)}`}>
                      {purchase?.status ? (purchase?.status?.charAt(0)?.toUpperCase() + purchase?.status?.slice(1)) : 'Unknown'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {new Date(purchase?.date)?.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                  </p>
                  {purchase?.shipper && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Icon name="Truck" size={14} />
                      {renderShipperDisplay(purchase?.shipper)}
                    </div>
                  )}
                </div>
                
                <div className="text-right flex-shrink-0">
                  <p className="text-xl font-black text-black tabular-nums">
                    {formatPrice((parseFloat(purchase?.subtotal) || 0) + (parseFloat(purchase?.taxAmount) || 0) + (parseFloat(purchase?.shippingAmount) || 0) - (parseFloat(purchase?.appliedCredit) || 0))}
                  </p>
                  <div className="text-xs font-bold text-slate-600 mt-2 space-y-1 tabular-nums uppercase tracking-tighter">
                    <p className="flex items-center justify-end gap-2">
                      <span className="text-right min-w-[5rem]">Items:</span>
                      <span className="font-black text-black w-20">{formatPrice(purchase?.subtotal)}</span>
                    </p>
                    {purchase?.taxAmount > 0 && (
                      <p className="flex items-center justify-end gap-2">
                        <span className="text-right min-w-[5rem]">Tax (8.25%):</span>
                        <span className="font-black text-black w-20">{formatPrice(purchase?.taxAmount)}</span>
                      </p>
                    )}
                    <p className="flex items-center justify-end gap-2">
                      <span className="text-right min-w-[5rem]">Shipping:</span>
                      <span className="font-black text-black w-20">{purchase?.shippingAmount === 0 ? 'FREE' : formatPrice(purchase?.shippingAmount)}</span>
                    </p>
                    {purchase?.appliedCredit > 0 && (
                      <p className="text-green-700 flex items-center justify-end gap-2 bg-green-50 px-1 rounded">
                        <span className="text-right min-w-[5rem]">Credit Used:</span>
                        <span className="font-black w-20">-{formatPrice(purchase?.appliedCredit)}</span>
                      </p>
                    )}
                    <div className="pt-1 mt-1 border-t-2 border-slate-900 flex items-center justify-end gap-2">
                       <span className="text-black font-black">Paid:</span>
                       <span className="text-primary font-black w-20 text-sm">{formatPrice((parseFloat(purchase?.subtotal) || 0) + (parseFloat(purchase?.taxAmount) || 0) + (parseFloat(purchase?.shippingAmount) || 0) - (parseFloat(purchase?.appliedCredit) || 0))}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {purchase?.items?.map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                      <Image
                        src={item?.image}
                        alt={item?.imageAlt}
                        className="w-full h-full object-cover" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-foreground truncate">
                        {item?.name}
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        {item?.type} × {item?.quantity}
                      </p>
                      {/* ✅ UPDATED: Beautifully styled, highly interactive full-row hover badge with clear user instructions and live status pill */}
                      {(() => {
                        let info = item?.shipper;
                        if (typeof info === 'string') {
                          try { info = JSON.parse(info); } catch (e) { info = null; }
                        }
                        const trackingNumber = info?.tracking_number;
                        const carrier = info?.carrier || 'Carrier';
                        const isItemDelivered = (item?.itemStatus || item?.item_status || '').toLowerCase() === 'delivered';
                        const isItemShipped = (item?.itemStatus || item?.item_status || '').toLowerCase() === 'shipped' || !!trackingNumber;
                        
                        return (
                          <div 
                            onClick={() => handleTrackingClick(info, item)}
                            className="text-xs text-muted-foreground flex flex-wrap items-center gap-2 mt-2 px-3 py-2 bg-slate-50 border border-slate-100 hover:bg-primary/5 hover:border-primary/20 hover:text-primary rounded-lg cursor-pointer transition group w-fit"
                            title="Click to view live Shipment Tracking timeline & details"
                          >
                            <Icon name="Truck" size={14} className="text-gray-400 group-hover:text-primary group-hover:scale-110 transition shrink-0" />
                            <span className="font-semibold text-gray-700 group-hover:text-primary">
                              Shipping via {carrier}:
                            </span>
                            {trackingNumber ? (
                              <span className="font-mono font-black text-primary hover:underline">
                                {trackingNumber}
                              </span>
                            ) : (
                              <span className="text-amber-600 font-bold bg-amber-50 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-tighter border border-amber-200">
                                {item.manufacturingStatus && item.manufacturingStatus !== 'Pending' ? item.manufacturingStatus : 'Awaiting Production'}
                              </span>
                            )}

                            {/* Live Delivery / Transit Status Pill Badge */}
                            {isItemDelivered ? (
                              <span className="bg-emerald-100 text-emerald-800 border border-emerald-300 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider flex items-center gap-1 shadow-sm">
                                <Icon name="CheckCircle" size={11} className="text-emerald-600" /> Delivered
                              </span>
                            ) : isItemShipped ? (
                              <span className="bg-blue-100 text-blue-800 border border-blue-300 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider flex items-center gap-1 shadow-sm">
                                <Icon name="Truck" size={11} className="text-blue-600" /> In Transit
                              </span>
                            ) : null}

                            <span className="text-[10px] text-muted-foreground/80 group-hover:text-primary/80 ml-1.5 font-medium flex items-center gap-0.5">
                              • <Icon name="Info" size={11} /> (Click for Live Timeline)
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                    
                    <div className="text-right flex-shrink-0 tabular-nums">
                      <p className="text-sm font-medium text-foreground">
                        {formatPrice(item?.price)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-end mt-4 pt-4 border-t border-border gap-3">
                <Button 
                  variant="outline" 
                  size="sm" 
                  iconName="Download"
                  onClick={() => handleDownloadPDF(purchase)}
                >
                  Download
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  iconName="FileText"
                  onClick={() => handleShowDetails(purchase)}
                >
                  Detail
                </Button>
              </div>
            </div>
          ))}
        </div>
        {filteredPurchases?.length === 0 &&
        <div className="text-center py-12">
            <Icon name="ShoppingBag" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No purchases found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {filter === 'all' ? "You haven't made any purchases yet." :
            `No ${filter} orders found.`}
            </p>
            <Button 
              variant="default" 
              iconName="Search"
              onClick={handleBrowseModels}
            >
              Browse Models
            </Button>
          </div>
        }
      </div>

      {/* ✅ UPDATED: Order Details Modal with per-item shipper */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-lg border border-border shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground">Order Details</h2>
              <button 
                onClick={handleCloseDetails}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={24} />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Order Information */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Order Number</span>
                  <span className="text-sm font-medium text-foreground">#{selectedOrder?.order_number || selectedOrder?.id}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Order Date</span>
                  <span className="text-sm font-medium text-foreground">
                    {new Date(selectedOrder?.date)?.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedOrder?.status)}`}>
                    {selectedOrder?.status ? (selectedOrder?.status?.charAt(0)?.toUpperCase() + selectedOrder?.status?.slice(1)) : 'Unknown'}
                  </span>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <h3 className="text-sm font-semibold text-foreground mb-4">Order Items</h3>
                <div className="space-y-4">
                  {selectedOrder?.items?.map((item, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                        <Image
                          src={item?.image}
                          alt={item?.imageAlt}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-foreground mb-1">
                          {item?.name}
                        </h4>
                        <p className="text-xs text-muted-foreground mb-2">
                          Type: {item?.type}
                        </p>
                        {/* ✅ UPDATED: Beautifully styled, highly interactive full-row hover badge for the details modal with live status pill */}
                        {(() => {
                          let info = item?.shipper;
                          if (typeof info === 'string') {
                            try { info = JSON.parse(info); } catch (e) { info = null; }
                          }
                          if (!info) return null;
                          const trackingNumber = info?.tracking_number;
                          const carrier = info?.carrier || 'Carrier';
                          const isItemDelivered = (item?.itemStatus || item?.item_status || '').toLowerCase() === 'delivered';
                          const isItemShipped = (item?.itemStatus || item?.item_status || '').toLowerCase() === 'shipped' || !!trackingNumber;
                          
                          return (
                            <div 
                              onClick={() => handleTrackingClick(info, item)}
                              className="text-xs text-muted-foreground flex flex-wrap items-center gap-2 mb-3 px-3 py-2 bg-slate-50 border border-slate-100 hover:bg-primary/5 hover:border-primary/20 hover:text-primary rounded-lg cursor-pointer transition group w-fit"
                              title="Click to view live Shipment Tracking timeline & details"
                            >
                              <Icon name="Truck" size={14} className="text-gray-400 group-hover:text-primary group-hover:scale-110 transition shrink-0" />
                              <span className="font-semibold text-gray-700 group-hover:text-primary">
                                Shipping via {carrier}:
                              </span>
                              {trackingNumber ? (
                                <span className="font-mono font-black text-primary hover:underline">
                                  {trackingNumber}
                                </span>
                              ) : (
                                <span className="text-amber-600 font-bold bg-amber-50 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-tighter border border-amber-200">
                                  {item.manufacturingStatus && item.manufacturingStatus !== 'Pending' ? item.manufacturingStatus : 'Awaiting Production'}
                                </span>
                              )}

                              {/* Live Delivery / Transit Status Pill Badge */}
                              {isItemDelivered ? (
                                <span className="bg-emerald-100 text-emerald-800 border border-emerald-300 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider flex items-center gap-1 shadow-sm">
                                  <Icon name="CheckCircle" size={11} className="text-emerald-600" /> Delivered
                                </span>
                              ) : isItemShipped ? (
                                <span className="bg-blue-100 text-blue-800 border border-blue-300 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider flex items-center gap-1 shadow-sm">
                                  <Icon name="Truck" size={11} className="text-blue-600" /> In Transit
                                </span>
                              ) : null}

                              <span className="text-[10px] text-muted-foreground/80 group-hover:text-primary/80 ml-1.5 font-medium flex items-center gap-0.5">
                                • <Icon name="Info" size={11} /> (Click for Live Timeline)
                              </span>
                            </div>
                          );
                        })()}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            Quantity: {item?.quantity}
                          </span>
                          <span className="text-sm font-medium text-foreground tabular-nums">
                            {formatPrice(item?.price * item?.quantity)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-border pt-4 space-y-2">
                <div className="flex items-center justify-between text-sm text-slate-600">
                  <span>Subtotal</span>
                  <span className="font-bold tabular-nums">
                    {formatPrice(selectedOrder?.subtotal)}
                  </span>
                </div>
                {selectedOrder?.taxAmount !== undefined && selectedOrder?.taxAmount !== null && (
                  <div className="flex items-center justify-between text-sm text-slate-600">
                    <span>Tax (8.25%)</span>
                    <span className="font-bold tabular-nums">
                      {formatPrice(selectedOrder?.taxAmount)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm text-slate-600">
                  <span>Shipping</span>
                  <span className="font-bold tabular-nums">
                    {selectedOrder?.shippingAmount === 0 ? 'FREE' : formatPrice(selectedOrder?.shippingAmount)}
                  </span>
                </div>
                {selectedOrder?.appliedCredit > 0 && (
                  <div className="flex items-center justify-between text-sm text-green-700 bg-green-50 px-2 py-1 rounded">
                    <span className="font-bold">Store Credit Applied</span>
                    <span className="font-black tabular-nums">
                      -{formatPrice(selectedOrder?.appliedCredit)}
                    </span>
                  </div>
                )}
                {selectedOrder?.discountCode && (
                  <div className="flex items-center justify-between text-sm text-blue-700 bg-blue-50 px-2 py-1 rounded">
                    <span className="font-bold">Discount ({selectedOrder.discountCode})</span>
                    <span className="font-black tabular-nums">
                      -{formatPrice(selectedOrder?.discountAmount || 0)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-4 border-t-2 border-slate-900">
                  <span className="text-lg font-black text-black uppercase">Total Paid</span>
                  <span className="text-2xl font-black text-primary tabular-nums">
                    {formatPrice((parseFloat(selectedOrder?.subtotal) || 0) + (parseFloat(selectedOrder?.taxAmount) || 0) + (parseFloat(selectedOrder?.shippingAmount) || 0) - (parseFloat(selectedOrder?.appliedCredit) || 0))}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => handleDownloadPDF(selectedOrder)}
                  iconName="Download"
                >
                  Download PDF
                </Button>
                <Button 
                  variant="default" 
                  onClick={handleCloseDetails}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reusable Shipment Tracking Details Modal */}
      <ShipmentTrackingModal
        isOpen={!!activeTracking}
        onClose={() => setActiveTracking(null)}
        carrier={activeTracking?.carrier}
        trackingNumber={activeTracking?.trackingNumber}
        serviceType={activeTracking?.serviceType}
        parentItem={activeTracking?.parentItem}
        onDelivered={({ trackingNumber }) => {
          setPurchases(prevPurchases => 
            prevPurchases.map(p => {
              let orderHasThisItem = false;
              const updatedItems = p.items.map(it => {
                let itTrack = it.shipper?.tracking_number;
                if (!itTrack && typeof it.shipper === 'string') {
                  try { itTrack = JSON.parse(it.shipper)?.tracking_number; } catch(e) {}
                }
                if (itTrack === trackingNumber || (activeTracking?.parentItem && it.id === activeTracking.parentItem.id)) {
                  orderHasThisItem = true;
                  return { ...it, itemStatus: 'delivered', item_status: 'delivered' };
                }
                return it;
              });

              if (orderHasThisItem) {
                const allDelivered = updatedItems.every(it => it.itemStatus === 'delivered' || it.item_status === 'delivered');
                return {
                  ...p,
                  status: allDelivered ? 'delivered' : p.status,
                  items: updatedItems
                };
              }
              return p;
            })
          );

          if (activeTracking?.parentItem?.id) {
            supabase.from('order_items').update({ item_status: 'delivered' }).eq('id', activeTracking.parentItem.id).then();
          }
        }}
      />
    </>
  );
};

export default PurchaseHistory;